import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { useSiteConfig } from "@/context/SiteConfig";
import { useLang, Reveal } from "@/i18n/site";
import { toast } from "sonner";
import {
  ArrowRight, Wrench, Cog, ShieldCheck, PackageSearch, Network, GraduationCap,
  Wind, Factory, Car, Zap, HardHat, Settings2, CheckCircle2, Boxes, Handshake,
  Lightbulb, UserCog, Building2, Paperclip, Send, Mail, MessageCircle,
} from "lucide-react";

const IMG = {
  hero: "https://images.pexels.com/photos/12956302/pexels-photo-12956302.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
  wind: "https://images.pexels.com/photos/12956303/pexels-photo-12956303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ompec: "https://images.unsplash.com/photo-1729101199373-08ef5221cb87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  engineer: "https://images.pexels.com/photos/29988955/pexels-photo-29988955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  factory: "https://images.pexels.com/photos/14804687/pexels-photo-14804687.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  measure: "https://images.unsplash.com/photo-1564182842834-681b7be6de4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  ladder: "https://images.unsplash.com/photo-1611845103756-e06286422869?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  machinist: "https://images.unsplash.com/photo-1565954786194-d22abeaac3ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const PILLAR_ICONS = { supply: PackageSearch, solve: Cog, protect: ShieldCheck };
const HELP_ICONS = [Wrench, Settings2, GraduationCap, Network];
const IND_ICONS = [Wind, Factory, Car, Zap, HardHat, Settings2];
const IND_IMGS = [IMG.wind, IMG.factory, IMG.machinist, IMG.ladder, IMG.measure, IMG.engineer];
const VAL_ICONS = [Boxes, Lightbulb, UserCog, Handshake];

function SectionTitle({ children, light }) {
  return <h2 className={`font-display text-3xl md:text-5xl font-extrabold tracking-tight ${light ? "text-white" : "text-[var(--wk-primary)]"}`}>{children}</h2>;
}

function HeroOverlay() {
  const { hero_overlay_active, hero_overlay_intensity } = useSiteConfig();
  if (hero_overlay_active === false) return null;
  const LEVELS = {
    suave: { side: "from-black/55 via-black/15 to-transparent", scrim: "from-black/25 via-transparent to-transparent" },
    media: { side: "from-black/80 via-black/35 to-transparent", scrim: "from-black/45 via-transparent to-transparent" },
    fuerte: { side: "from-black/95 via-black/55 to-transparent", scrim: "from-black/60 via-transparent to-transparent" },
  };
  const lv = LEVELS[hero_overlay_intensity] || LEVELS.media;
  return (
    <>
      <div className={`absolute inset-0 bg-gradient-to-r ${lv.side}`} data-testid="hero-overlay" />
      <div className={`absolute inset-0 bg-gradient-to-t ${lv.scrim}`} data-testid="hero-scrim" />
    </>
  );
}

function HeroBackground() {
  const { hero_images, hero_interval, hero_transition } = useSiteConfig();
  const imgs = hero_images && hero_images.length ? hero_images : [IMG.hero];
  const mode = hero_transition || "crossfade";
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (imgs.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), (hero_interval || 7) * 1000);
    return () => clearInterval(t);
  }, [imgs.length, hero_interval]);
  const dur = mode === "crossfade" ? "duration-[1600ms]" : "duration-[900ms]";
  return (
    <div className="absolute inset-0 overflow-hidden bg-[var(--wk-primary)] [filter:saturate(1.08)_contrast(1.04)]" data-testid="hero-carousel" data-transition={mode}>
      {imgs.map((src, i) => {
        const active = i === idx;
        if (mode === "slide") {
          return (
            <img key={src + i} src={src} alt="Portada West KAC"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-in-out"
              style={{ transform: `translateX(${(i - idx) * 100}%)` }} />
          );
        }
        return (
          <img key={src + i} src={src} alt="Portada West KAC"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity ${dur} ease-in-out ${active ? "opacity-100" : "opacity-0"} ${mode === "kenburns" && active ? "wk-kenburns" : ""}`} />
        );
      })}
    </div>
  );
}

function BrandsSection() {
  const { t } = useLang();
  const { brand_logos } = useSiteConfig();
  const logos = brand_logos || [];
  return (
    <section id="marcas" className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <Reveal className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--wk-primary)]">{t.brands.title}</h2>
          <p className="mt-2 text-sm text-slate-500">{t.brands.sub}</p>
        </Reveal>
        {logos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="brands-grid">
            {logos.map((src, i) => (
              <Reveal key={src + i} delay={i * 50} className="h-20 grid place-items-center rounded-sm border border-slate-200 bg-white p-4" data-testid={`brand-logo-${i}`}>
                <img src={src} alt={`Marca ${i + 1}`} className="max-h-12 max-w-full object-contain" loading="lazy" />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Reveal key={i} delay={i * 50} className="h-20 grid place-items-center rounded-sm border border-dashed border-slate-300 bg-[var(--wk-surface)] text-slate-400 text-xs font-medium" data-testid={`brand-${i}`}>
                {t.brands.pending}
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <PublicLayout>
      {/* HERO */}
      <section id="inicio" className="relative min-h-[86vh] flex items-center" data-testid="hero">
        <HeroBackground />
        <HeroOverlay />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <Reveal className="max-w-3xl [text-shadow:0_2px_12px_rgba(0,0,0,0.55)]">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--wk-accent)] font-semibold mb-5">{t.hero.eyebrow}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] [text-shadow:0_3px_18px_rgba(0,0,0,0.6)]">{t.hero.title}</h1>
            <div className="mt-5 inline-flex items-center gap-3 text-white font-display font-bold tracking-[0.15em] text-lg sm:text-xl">
              <span>SUPPLY.</span><span className="text-[var(--wk-accent)]">SOLVE.</span><span>PROTECT.</span>
            </div>
            <p className="mt-6 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl [text-shadow:0_2px_10px_rgba(0,0,0,0.5)]">{t.hero.desc}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href="#contacto" onClick={(e) => { e.preventDefault(); document.getElementById("contacto").scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center justify-center gap-2 bg-[var(--wk-cta)] hover:bg-[var(--wk-cta-hover)] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors" data-testid="hero-quote-btn">
                {t.cta.quote} <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#soluciones" onClick={(e) => { e.preventDefault(); document.getElementById("soluciones").scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center justify-center gap-2 border border-white/40 hover:bg-white/10 text-white font-semibold px-7 py-3.5 rounded-sm transition-colors" data-testid="hero-solutions-btn">
                {t.cta.solutions}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUPPLY / SOLVE / PROTECT */}
      <section id="soluciones" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <Reveal className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-[var(--wk-accent)] font-semibold mb-3">{t.pillars.subtitle}</div>
          <SectionTitle>{t.pillars.title}</SectionTitle>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {t.pillars.items.map((p, i) => {
            const Icon = PILLAR_ICONS[p.key];
            const center = p.key === "solve";
            return (
              <Reveal key={p.key} delay={i * 90} className={`p-9 md:p-10 rounded-sm border ${center ? "bg-[var(--wk-secondary)] text-white border-[var(--wk-secondary)]" : "bg-white border-slate-200"}`} data-testid={`pillar-${p.key}`}>
                <div className={`w-12 h-12 grid place-items-center rounded-sm mb-6 ${p.key === "supply" ? "bg-[var(--wk-primary)]" : "bg-[var(--wk-accent)]"}`}><Icon className="w-6 h-6 text-white" /></div>
                <div className={`font-display font-extrabold tracking-[0.15em] text-sm mb-1 ${center ? "text-[var(--wk-accent)]" : "text-[var(--wk-accent)]"}`}>{p.tag}</div>
                <h3 className={`font-display text-2xl font-bold mb-3 ${center ? "text-white" : "text-[var(--wk-primary)]"}`}>{p.h}</h3>
                <p className={`text-sm leading-relaxed mb-6 ${center ? "text-white/70" : "text-slate-600"}`}>{p.d}</p>
                <ul className="space-y-2 mb-7">
                  {p.list.map((li) => (
                    <li key={li} className={`flex items-center gap-2 text-sm ${center ? "text-white/85" : "text-slate-700"}`}>
                      <CheckCircle2 className="w-4 h-4 text-[var(--wk-accent)] shrink-0" /> {li}
                    </li>
                  ))}
                </ul>
                <a href={p.key === "protect" ? "#ompec" : "#servicios"} onClick={(e) => { e.preventDefault(); document.getElementById(p.key === "protect" ? "ompec" : "servicios").scrollIntoView({ behavior: "smooth" }); }}
                  className={`inline-flex items-center gap-2 text-sm font-semibold ${center ? "text-white" : "text-[var(--wk-primary)]"} hover:text-[var(--wk-accent)] transition-colors`}>
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </a>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* HOW CAN WE HELP */}
      <section id="servicios" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <Reveal className="mb-14"><SectionTitle>{t.help.title}</SectionTitle></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.help.items.map((c, i) => {
              const Icon = HELP_ICONS[i];
              return (
                <Reveal key={i} delay={i * 80} className="group p-8 rounded-sm border border-slate-200 hover:border-[var(--wk-primary)] bg-[var(--wk-surface)] transition-colors" data-testid={`help-card-${i}`}>
                  <Icon className="w-8 h-8 text-[var(--wk-accent)] mb-5" />
                  <h3 className="font-display text-lg font-bold text-[var(--wk-primary)] mb-2">{c.h}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HISTORY / EVOLUTION */}
      <section id="nosotros" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="relative rounded-sm overflow-hidden aspect-[4/3] border border-slate-200">
            <img src={IMG.engineer} alt="Ingeniero industrial en campo" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--wk-primary)]/40 to-transparent" />
          </Reveal>
          <Reveal delay={100}>
            <SectionTitle>{t.history.title}</SectionTitle>
            <div className="mt-6 space-y-4">
              {t.history.p.map((para, i) => <p key={i} className="text-slate-600 leading-relaxed">{para}</p>)}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-2">
              {t.history.steps.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span className={`px-3.5 py-2 rounded-sm text-sm font-semibold ${i === t.history.steps.length - 1 ? "bg-[var(--wk-accent)] text-white" : "bg-white border border-slate-200 text-[var(--wk-primary)]"}`}>{s}</span>
                  {i < t.history.steps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-400" />}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industrias" className="bg-[var(--wk-primary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <Reveal className="mb-14 max-w-2xl"><SectionTitle light>{t.industries.title}</SectionTitle></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.industries.items.map((it, i) => {
              const Icon = IND_ICONS[i];
              return (
                <Reveal key={i} delay={i * 70} className="group relative rounded-sm overflow-hidden h-64 border border-white/10" data-testid={`industry-${i}`}>
                  <img src={IND_IMGS[i]} alt={it.h} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <Icon className="w-7 h-7 text-[var(--wk-accent)] mb-2" />
                    <h3 className="font-display text-lg font-bold text-white">{it.h}</h3>
                    <p className="text-xs text-white/75 leading-relaxed mt-1.5 max-h-0 opacity-0 overflow-hidden group-hover:max-h-24 group-hover:opacity-100 transition-all duration-300">{it.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* OM-PEC SAFETY */}
      <section id="ompec" className="relative bg-[var(--wk-primary)] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, var(--wk-accent) 0, var(--wk-accent) 18px, transparent 18px, transparent 36px)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--wk-accent)] font-semibold mb-3">{t.ompec.brand}</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{t.ompec.title}</h2>
            <p className="mt-4 text-xl font-display font-bold text-[var(--wk-accent)]">{t.ompec.claim}</p>
            <div className="mt-6 space-y-4">
              {t.ompec.p.map((para, i) => <p key={i} className="text-white/75 leading-relaxed">{para}</p>)}
            </div>
            <div className="mt-7 flex flex-wrap gap-2">
              {t.ompec.tags.map((tag) => <span key={tag} className="px-3 py-1.5 rounded-sm bg-white/5 border border-white/15 text-white/85 text-xs font-medium">{tag}</span>)}
            </div>
            <a href="#contacto" onClick={(e) => { e.preventDefault(); document.getElementById("contacto").scrollIntoView({ behavior: "smooth" }); }}
              className="mt-8 inline-flex items-center gap-2 bg-[var(--wk-accent)] hover:bg-[var(--wk-accent-hover)] text-[var(--wk-primary)] font-bold px-7 py-3.5 rounded-sm transition-colors" data-testid="ompec-cta">
              {t.cta.ompec} <ArrowRight className="w-4 h-4" />
            </a>
            <div className="mt-6 text-white/40 text-xs uppercase tracking-[0.2em]">{t.ompec.sub}</div>
          </Reveal>
          <Reveal delay={120} className="relative rounded-sm overflow-hidden aspect-[4/5] border border-white/10">
            <img src={IMG.ompec} alt="Trabajo en alturas y protección contra caídas" className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-[var(--wk-surface)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <Reveal className="max-w-2xl mb-14">
          <SectionTitle>{t.values.title}</SectionTitle>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">{t.values.lead}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {t.values.items.map((v, i) => {
            const Icon = VAL_ICONS[i];
            return (
              <Reveal key={i} delay={i * 80} className="flex gap-5 p-8 rounded-sm border border-slate-200 bg-white" data-testid={`value-${i}`}>
                <div className="w-12 h-12 shrink-0 grid place-items-center rounded-sm bg-[var(--wk-surface)] border border-slate-200"><Icon className="w-6 h-6 text-[var(--wk-accent)]" /></div>
                <div>
                  <h3 className="font-display text-lg font-bold text-[var(--wk-primary)] mb-1.5">{v.h}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        </div>
      </section>

      {/* BRANDS */}
      <BrandsSection />

      {/* QUOTE + CONTACT */}
      <QuoteContact />
    </PublicLayout>
  );
}

function QuoteContact() {
  const { t } = useLang();
  const { contact_email, whatsapp_phone } = useSiteConfig();
  const empty = { nombre: "", empresa: "", email: "", telefono: "", pais: "", industria: "", tipo: "", descripcion: "", privacidad: false };
  const [f, setF] = useState(empty);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    const sel = e.target.files?.[0];
    if (!sel) return;
    if (sel.size > 10 * 1024 * 1024) { toast.error("Máx. 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", sel);
      const { data } = await api.post("/public/quote-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile({ id: data.file_id, name: data.filename });
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nombre || !f.email || !f.privacidad) { toast.error(t.quote.err); return; }
    setBusy(true);
    try {
      const { privacidad, ...payload } = f;
      await api.post("/public/quote-request", { ...payload, file_id: file?.id || null, file_name: file?.name || null });
      setSent(true);
      toast.success(t.quote.ok);
      setF(empty); setFile(null);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[var(--wk-primary)] focus:border-[var(--wk-primary)] transition";

  return (
    <section id="contacto" className="bg-[var(--wk-surface)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <Reveal>
          <SectionTitle>{t.quote.title}</SectionTitle>
          <p className="mt-5 text-slate-600 leading-relaxed">{t.quote.desc}</p>
          <div className="mt-6 p-4 rounded-sm border border-slate-200 bg-white text-sm text-slate-600 flex items-start gap-3">
            <Paperclip className="w-5 h-5 text-[var(--wk-accent)] shrink-0 mt-0.5" /> {t.quote.note}
          </div>
          <div className="mt-10 pt-8 border-t border-slate-200">
            <h3 className="font-display text-2xl font-bold text-[var(--wk-primary)]">{t.contact.title}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{t.contact.desc}</p>
            <div className="mt-5 flex items-center gap-3 text-[var(--wk-primary)]">
              <Building2 className="w-5 h-5 text-[var(--wk-accent)]" />
              <div>
                <div className="font-display font-bold">WEST KAC COMPANY · {t.contact.country}</div>
                <div className="text-xs text-slate-500">{t.contact.countryNote}</div>
              </div>
            </div>
            <div className="mt-4 space-y-2.5 text-sm">
              <a href={`mailto:${contact_email}`} className="flex items-center gap-3 text-[var(--wk-primary)] hover:text-[var(--wk-accent)] transition-colors" data-testid="contact-email">
                <Mail className="w-5 h-5 text-[var(--wk-accent)]" /> {contact_email}
              </a>
              {whatsapp_phone && (
                <a href={`https://wa.me/${whatsapp_phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[var(--wk-primary)] hover:text-[var(--wk-accent)] transition-colors" data-testid="contact-whatsapp">
                  <MessageCircle className="w-5 h-5 text-[var(--wk-accent)]" /> WhatsApp: {whatsapp_phone}
                </a>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={100} className="rounded-sm border border-slate-200 bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {sent ? (
            <div className="text-center py-14" data-testid="quote-success">
              <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-emerald-50 mb-5"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
              <p className="text-lg font-display font-bold text-[var(--wk-primary)]">{t.quote.ok}</p>
              <button onClick={() => setSent(false)} className="mt-6 text-sm font-semibold text-[var(--wk-accent)]" data-testid="quote-new">+ {t.cta.quote}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.nombre} *</label><input className={inputCls} value={f.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="qf-nombre" required /></div>
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.empresa}</label><input className={inputCls} value={f.empresa} onChange={(e) => set("empresa", e.target.value)} data-testid="qf-empresa" /></div>
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.email} *</label><input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="qf-email" required /></div>
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.telefono}</label><input className={inputCls} value={f.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="qf-telefono" /></div>
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.pais}</label><input className={inputCls} value={f.pais} onChange={(e) => set("pais", e.target.value)} data-testid="qf-pais" /></div>
                <div><label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.industria}</label><input className={inputCls} value={f.industria} onChange={(e) => set("industria", e.target.value)} data-testid="qf-industria" /></div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.tipo}</label>
                <select className={inputCls} value={f.tipo} onChange={(e) => set("tipo", e.target.value)} data-testid="qf-tipo">
                  <option value="">—</option>
                  {t.quote.f.tipos.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--wk-primary)] mb-1.5">{t.quote.f.descripcion}</label>
                <textarea rows={4} className={inputCls} value={f.descripcion} onChange={(e) => set("descripcion", e.target.value)} data-testid="qf-descripcion" />
              </div>
              <div>
                <label className="flex items-center justify-between gap-2 rounded-sm border border-dashed border-slate-300 px-3.5 py-3 cursor-pointer hover:border-[var(--wk-primary)] transition-colors">
                  <span className="inline-flex items-center gap-2 text-sm text-slate-600 truncate min-w-0">
                    <Paperclip className="w-4 h-4 text-[var(--wk-accent)] shrink-0" />
                    {uploading ? "Subiendo..." : file ? file.name : t.quote.note}
                  </span>
                  <input type="file" className="hidden" onChange={onFile} data-testid="qf-file"
                    accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.zip" />
                  {file
                    ? <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }} className="text-xs font-semibold text-red-600 shrink-0" data-testid="qf-file-remove">Quitar</button>
                    : <span className="text-xs font-semibold text-[var(--wk-primary)] shrink-0">Adjuntar</span>}
                </label>
              </div>
              <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={f.privacidad} onChange={(e) => set("privacidad", e.target.checked)} className="mt-0.5" data-testid="qf-privacidad" />
                <span>{t.quote.f.privacidad}</span>
              </label>
              <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[var(--wk-cta)] hover:bg-[var(--wk-cta-hover)] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-sm transition-colors" data-testid="qf-submit">
                <Send className="w-4 h-4" /> {t.quote.f.submit}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
